import { z } from 'zod';
import { moneySchema } from '../money';
import { listQuerySchema } from '../common/pagination';
import { costCenterSchema, timerProjectKindSchema } from './master-data.schema';

/**
 * Timer starten: Self-Service — Mitarbeiter wird serverseitig aus dem User
 * aufgelöst, die Kostenstelle aus dem Mitarbeiterstamm übernommen (nicht wählbar).
 * Auswahl: Firma (Gesellschaft) → Projekt (echtes ProERP- oder Zeit-Projekt) →
 * Unterkategorie.
 */
export const startTimerSchema = z.object({
  /** Gesellschaft (LegalEntity aus den Einstellungen). */
  legalEntityId: z.string().uuid(),
  /** Kategorie (z. B. „Aufträge", „Vertrieb") — schränkt Projekt- und Tätigkeitsauswahl ein. */
  categoryId: z.string().uuid(),
  /** Quelle des gewählten Projekts: echtes ProERP-Projekt oder Zeit-Projekt. */
  projectKind: timerProjectKindSchema,
  projectId: z.string().uuid(),
  ttSubCategoryId: z.string().uuid().nullable().optional(),
  note: z.string().trim().max(500).nullable().optional(),
});
export type StartTimerInput = z.infer<typeof startTimerSchema>;

/** Timer stoppen: optionale letzte Anpassung der Bemerkung. */
export const stopTimerSchema = z.object({
  note: z.string().trim().max(500).nullable().optional(),
});
export type StopTimerInput = z.infer<typeof stopTimerSchema>;

/**
 * Zeiteintrag manuell anlegen (ohne Stoppuhr) — z. B. nachträglich erfasste
 * Stunden. Anders als beim Timer-Start sind Start UND Ende schon bekannt und
 * Pflicht; Kategorie ist wie beim Start Pflicht (Neustart ab Kategorie-Einführung,
 * kein Alt-Verhalten für neu angelegte Einträge).
 */
export const createManualTimeEntrySchema = z.object({
  legalEntityId: z.string().uuid(),
  categoryId: z.string().uuid(),
  projectKind: timerProjectKindSchema,
  projectId: z.string().uuid(),
  ttSubCategoryId: z.string().uuid().nullable().optional(),
  startTime: z.string().datetime(),
  endTime: z.string().datetime(),
  note: z.string().trim().max(500).nullable().optional(),
});
export type CreateManualTimeEntryInput = z.infer<typeof createManualTimeEntrySchema>;

/** Antwort eines Zeiterfassungs-Eintrags (Stoppuhr). */
export const timeTrackingEntryResponseSchema = z.object({
  id: z.string().uuid(),
  employeeId: z.string().uuid(),
  employeeName: z.string(),
  date: z.string().datetime(),
  startTime: z.string().datetime().nullable(),
  endTime: z.string().datetime().nullable(),
  /** true, solange der Timer läuft (startTime gesetzt, endTime null). */
  isRunning: z.boolean(),
  /** Dauer in Sekunden (für laufende Timer aus startTime bis jetzt). */
  durationSeconds: z.number().int(),
  hours: z.number(),
  /** Kostenstelle (Snapshot aus dem Mitarbeiterstamm beim Start). */
  costCenter: costCenterSchema.nullable(),
  /** Firma = Gesellschaft (LegalEntity) als Snapshot. */
  legalEntityId: z.string().uuid().nullable(),
  legalEntityName: z.string().nullable(),
  /** Kategorie-Snapshot — null bei Einträgen von vor der Kategorie-Einführung. */
  ttCategoryId: z.string().uuid().nullable(),
  ttCategoryName: z.string().nullable(),
  /** Zeit-Projekt-Snapshot (gesetzt, wenn ein Zeiterfassungs-eigenes Projekt gewählt wurde). */
  ttProjectId: z.string().uuid().nullable(),
  ttProjectName: z.string().nullable(),
  ttSubCategoryId: z.string().uuid().nullable(),
  ttSubCategoryName: z.string().nullable(),
  /** Verknüpftes zentrales ProERP-Projekt (Snapshot) — gesetzt bei echtem Projekt
   *  oder bei Hybrid-Verknüpfung eines Zeit-Projekts. */
  projectId: z.string().uuid().nullable(),
  projectCode: z.string().nullable(),
  projectName: z.string().nullable(),
  /** Ist-Kosten dieser Buchung (Stunden × Satz, Snapshot). null = kein Satz hinterlegt. */
  cost: moneySchema.nullable(),
  note: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type TimeTrackingEntryResponse = z.infer<typeof timeTrackingEntryResponseSchema>;

/** Eintrag bearbeiten: Start/Ende, Dimensionen, Bemerkung (Kostenstelle bleibt fix). */
export const updateTimeTrackingEntrySchema = z.object({
  startTime: z.string().datetime().optional(),
  endTime: z.string().datetime().nullable().optional(),
  legalEntityId: z.string().uuid().optional(),
  // Optional statt Pflichtfeld (anders als beim Start): ältere Einträge ohne
  // Kategorie dürfen weiter bearbeitet werden, ohne sie nachträglich erzwingen.
  categoryId: z.string().uuid().optional(),
  projectKind: timerProjectKindSchema.optional(),
  projectId: z.string().uuid().optional(),
  ttSubCategoryId: z.string().uuid().nullable().optional(),
  note: z.string().trim().max(500).nullable().optional(),
});
export type UpdateTimeTrackingEntryInput = z.infer<typeof updateTimeTrackingEntrySchema>;

/** Listen-Query der eigenen Einträge: Pagination + Datums-/Firmen-Filter. */
export const timeTrackingEntriesQuerySchema = listQuerySchema.extend({
  dateFrom: z.string().date().optional(),
  dateTo: z.string().date().optional(),
  legalEntityId: z.string().uuid().optional(),
});
export type TimeTrackingEntriesQuery = z.infer<typeof timeTrackingEntriesQuerySchema>;
